import { Box, Group, Skeleton, Stack } from '@mantine/core';
import { DeviceGetResponse } from 'api/actions/device-get/device-get-response';
import { useApi } from 'api/api-context';
import Toast from 'components/Toast';
import DeviceTypeIcon from 'components/device/DeviceTypeIcon';
import { translateRevisionPeriod } from 'components/inputs/revision-period-select/RevisionPeriodSelect';
import P1Regular from 'components/typography/P1Regular';
import P3Regular from 'components/typography/P3Regular';
import panic from 'errors/panic';
import { useEffect, useMemo, useState } from 'react';
import { useResponsiveDesign } from 'hooks/use-responsive-design';

interface ParentDeviceRevisionPlansProps {
  parentDeviceId: string;
}

/**
 * Displays readonly revision plans configured on parent device.
 */
export default function ParentDeviceRevisionPlans({ parentDeviceId }: ParentDeviceRevisionPlansProps) {
  const { isTablet } = useResponsiveDesign();
  const { getAction } = useApi();
  const [parentDevice, setParentDevice] = useState<DeviceGetResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getDevice = getAction('DeviceGet');

    setLoading(true);

    getDevice({
      parameters: { deviceId: parentDeviceId },
    })
      .then((device) => {
        setParentDevice(device);
      })
      .catch(panic)
      .finally(() => {
        setLoading(false);
      });
  }, [getAction, parentDeviceId]);

  const parentRevisionPlans = useMemo(
    () => parentDevice?.nextRevision?.filter((revision) => !revision.deleted) ?? [],
    [parentDevice?.nextRevision]
  );

  /**
   * Formats date string for readonly display.
   */
  function formatDate(date?: string) {
    if (!date) {
      return '';
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return '';
    }

    return parsedDate.toLocaleDateString('sk');
  }

  if (loading) {
    return (
      <Stack ml={40} mr={24} spacing={16}>
        <Skeleton height={112} radius="sm" />
      </Stack>
    );
  }

  if (parentRevisionPlans.length === 0) {
    return (
      <Box ml={40} mr={24}>
        <Toast
          type="info"
          size="s"
          withCloseButton={false}
          message="Nadradené zariadenie nemá nastavené žiadne plánované revízie."
        />
      </Box>
    );
  }

  return (
    <Stack spacing={16}>
      {parentRevisionPlans.map((revision, index) => (
        <Box
          key={revision.revisionPlanId ?? `${revision.description}-${revision.date}-${index}`}
          py={24}
          px={isTablet ? 20 : 40}
          ml={40}
          mr={24}
          bg="gray.0"
          sx={() => ({
            borderRadius: '4px',
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr',
            gap: '40px',
          })}
        >
          <Stack spacing={4}>
            <P3Regular color="gray.6">Úkon</P3Regular>
            <Group spacing={8}>
              <DeviceTypeIcon typeId={revision.deviceTypeId || parentDevice?.deviceType.deviceTypeId || 0} />
              <P1Regular color="gray.8">{revision.description || null}</P1Regular>
            </Group>
          </Stack>

          <Stack spacing={4}>
            <P3Regular color="gray.6">Nasledujúci termín</P3Regular>
            <P1Regular color="gray.8">{formatDate(revision.date)}</P1Regular>
          </Stack>

          <Stack spacing={4}>
            <P3Regular color="gray.6">Perióda</P3Regular>
            <P1Regular color="gray.8">{translateRevisionPeriod(revision.revisionPeriod)}</P1Regular>
          </Stack>

          <Stack spacing={4}>
            <P3Regular color="gray.6">Externé ID</P3Regular>
            <P1Regular color="gray.8">{revision.externalId || null}</P1Regular>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}
